import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from "react-redux";
import SwitchItem from "../SwitchItem";
import {updatePage} from "../../../actions/requests";
import Button from "../Button";
import TagInput from "../TagInput";
import './Moderation.scss'
import axios from "axios";
import {CSRF} from "../../../constants";
import {AbilityContext, errorNotify, successNotify} from "../../../utils/app";
import TestTab from "../TestTab";
import {useAbility} from "@casl/react";
import EmojiContainer from "../EmojiContainer";
import {faFaceSmile} from "@fortawesome/free-regular-svg-icons";
import {setShowEmojiModal} from "../../../actions/ui";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ReactTooltip from "react-tooltip";
import CopySettings from "./CopySettings";
import ModalWindow from "../ModalWindow";
import {useModerationForm} from './hooks/useModerationForm';
import {AdvancedSettingsSection} from './components/AdvancedSettingsSection';
import {DeleteModeModal} from './components/DeleteModeModal';
import {ImportSection} from './components/ImportSection';
import {faCheck, faPlus} from "@fortawesome/free-solid-svg-icons";
import {faSpinnerThird} from "@awesome.me/kit-a13c95776b/icons/modules/classic/thin";
import {faXmark} from "@fortawesome/free-solid-svg-icons";
import {Multiselect} from "multiselect-react-dropdown";
import {faXmarkLarge} from "@awesome.me/kit-a13c95776b/icons/modules/sharp/thin";

const Moderation = (props) => {
  const dispatch = useDispatch()
  const moderationForm = useRef()
  const ability = useAbility(AbilityContext)
  const hostname = window.location.hostname
  const page = useSelector(state => state.pages.facebook.activePage)
  const user = useSelector(state => state.user)
  const { account_level } = user

  const {
    pageAttributes,
    userAttributes,
    emojiList,
    emojiSel,
    showDeleteModeModal,
    handleChange,
    handleEmojiSelect,
    clickOutsideEmoji,
    enableAdvancedOption,
    closeDeleteModeModal,
    deleteModeModal,
    deleteModeHandler,
    handleDeleteMode,
    setEmojiList,
    setEmojiSel,
    setShowDeleteModeModal,
    loadingButtons,
    setButtonLoading
  } = useModerationForm(page, account_level.attributes, dispatch)

  // State for custom AI filter inputs
  const [customAiFilterInputs, setCustomAiFilterInputs] = useState(() => {
    const existingFilters = pageAttributes.custom_ai_filter_desc || []
    return existingFilters.length > 0 ? existingFilters : ['']
  })

  const enabledFilterOptions = Object.entries(pageAttributes.enabled_filters || {}).map(
      ([id, label]) => ({
        id,
        name: label
      })
  )

  const getInitialDeleteModeFilterIds = () => {
    const deleteList = pageAttributes.delete_mode_list || {}
    const enabled = pageAttributes.enabled_filters || {}

    const fromDeleteMode = Object.keys(deleteList)

    if (fromDeleteMode.length > 0) {
      // We have a saved subset – use only those (but keep in sync with enabled)
      return fromDeleteMode.filter((id) => enabled[id])
    }

    // Nothing saved yet → default to ALL enabled filters
    return Object.keys(enabled)
  }

  const [deleteModeFilterIds, setDeleteModeFilterIds] = useState(getInitialDeleteModeFilterIds)


  const addDeleteModeFilter = (selectedList, selectedItem) => {
    setDeleteModeFilterIds(prev =>
        prev.includes(selectedItem.id) ? prev : [...prev, selectedItem.id]
    )
  }

  const removeDeleteModeFilter = (selectedList, selectedItem) => {
    setDeleteModeFilterIds(prev =>
        prev.filter(id => id !== selectedItem.id)
    )
  }

  useEffect(() => {
    const deleteList = pageAttributes.delete_mode_list || {}
    const enabled = pageAttributes.enabled_filters || {}

    const fromDeleteMode = Object.keys(deleteList)

    if (fromDeleteMode.length > 0) {
      setDeleteModeFilterIds(fromDeleteMode.filter((id) => enabled[id]))
    } else {
      setDeleteModeFilterIds(Object.keys(enabled))
    }
  }, [pageAttributes.delete_mode_list, pageAttributes.enabled_filters])


  useEffect(() => {
    ReactTooltip.rebuild()
  }, [])

  useEffect(() => {
    emojiSel && setEmojiList(emojiList.concat(emojiSel))
  }, [emojiSel])

  useEffect(() => {
    pageAttributes.hide_all_emojis && setEmojiList([])
  }, [pageAttributes.hide_all_emojis])

  useEffect(() => {
    if (pageAttributes.emoji_list) {
      setEmojiList(pageAttributes.emoji_list);
    }
  }, [pageAttributes.emoji_list]);

  // Update custom AI filter inputs when page attributes change
  useEffect(() => {
    const existingFilters = pageAttributes.custom_ai_filter_desc || []
    if (existingFilters.length > 0) {
      setCustomAiFilterInputs(existingFilters)
    } else if (customAiFilterInputs.length === 0) {
      setCustomAiFilterInputs([''])
    }
  }, [pageAttributes.custom_ai_filter_desc])

  // Custom AI filter input handlers
  const addCustomAiFilterInput = () => {
    if (customAiFilterInputs.length < 3) {
      setCustomAiFilterInputs([...customAiFilterInputs, ''])
    }
  }

  const removeCustomAiFilterInput = (index) => {
    if (customAiFilterInputs.length > 1) {
      const newInputs = customAiFilterInputs.filter((_, i) => i !== index)
      setCustomAiFilterInputs(newInputs)
    }
  }

  const updateCustomAiFilterInput = (index, value) => {
    const newInputs = [...customAiFilterInputs]
    newInputs[index] = value
    setCustomAiFilterInputs(newInputs)
  }

  const saveCustomAiFilter = (e) => {
    e.preventDefault()
    
    // Filter out empty inputs
    const nonEmptyInputs = customAiFilterInputs.filter(input => input.trim() !== '')
    
    // Create form data
    const formData = new FormData()
    
    // Add the custom_ai_filter_desc as an array
    nonEmptyInputs.forEach((input) => {
      formData.append('page[custom_ai_filter_desc][]', input.trim())
    })
    
    // Add other required fields
    formData.append('page[custom_ai_filter]', pageAttributes.custom_ai_filter)
    
    // Set loading state
    setButtonLoading('custom_ai_filter_desc', true)
    
    // Dispatch the update
    dispatch(updatePage(page.details.id, formData, false, () => {
      setButtonLoading('custom_ai_filter_desc', false)
    }))
  }

  const startImport = (e) => {
    e.preventDefault()

    axios({
      method: 'post',
      url: `/main/start_import`,
      data: {page_id: page.details.id},
      headers: { 'Content-Type': 'application/json',
        'X-CSRF-Token': CSRF}
    }).then(response => {
      // successNotify('Import has been successfully started')

    }).catch(errors => {
          errorNotify(errors.response.data.error)
        }
    );
  }

  return (
    <>
      <CopySettings setEmojiList={setEmojiList}/>

      <div className="settings-form my-4">
        <form onSubmit={(e) => e.preventDefault()} ref={moderationForm}>
          {/* Profanity and Sentiment Settings */}
          {userAttributes.hide_profanity && (
              <SwitchItem
                title="Hide profanity"
                tooltip_place="bottom"
                value="true"
                checked={pageAttributes.hide_profanity}
                name="page[hide_profanity]"
                disabled={pageAttributes.private_mode}
                changeHandler={handleChange}
                badge="AI"
              />
          )}
          
          {userAttributes.hide_negative_sentiment && (
            <SwitchItem 
              title="Hide negativity"
              tooltip_place="bottom"
              value="true"
              checked={pageAttributes.hide_negative_sentiment}
              name="page[hide_negative_sentiment]"
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
              badge="AI"
            >
              {pageAttributes.hide_negative_sentiment && (pageAttributes.hide_negativity_advanced ? <>
                  <div className="ml-4 mt-3 collapse show">
                  </div>
                  <div className="row languages-select">
                    <div className="col-12">
                      <div className="fs-08 mb-3">
                        By default we don't consider page context. Enabling this option allows the filter to consider the surrounding page content, improving accuracy in detecting negativity.
                      </div>
                      <textarea className="form-control inp-text"
                                rows="5"
                                name="page[hide_negativity_context]"
                                id="hide_negativity_context"
                                key={page.details.id}
                                defaultValue={pageAttributes.hide_negativity_context}
                                placeholder="Tell us more about your page to give context"/>
                      <div className="d-flex justify-content-between mt-1 mb-4">
                        <div className="sent-text-extra">
                        </div>
                        <div className='ml-2'>
                          <Button name="button" className="btn btn-primary save-blacklist" clickHandler={(e) => {
                            e.target.dataset.buttonId = 'hide_negativity_context'
                            handleChange(e)
                          }}>
                            Save
                            {loadingButtons.has('hide_negativity_context') && <FontAwesomeIcon icon={faSpinnerThird} className="ms-2" spin={true}/>}
                          </Button>
                        </div>
                      </div>
                    </div>

                  </div>
                </> :
                <div className="row languages-select my-2">
                  <a href="#" className="fs-08" onClick={e => enableAdvancedOption(e, "hide_negativity")}>Advanced settings (by default we don't consider page context)</a>
                </div>
              )}
            </SwitchItem>
          )}

          <SwitchItem
              title="Hide custom topics"
              tooltip_place="bottom"
              value="true"
              checked={pageAttributes.custom_ai_filter}
              name="page[custom_ai_filter]"
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
              badge="AI"
          >
            {pageAttributes.custom_ai_filter && <>
                      <div className="ml-4 mt-3 collapse show">
                      </div>
                      <div className="row languages-select">
                        <div className="col-12">
                          <div className="sent-text-extra mb-3 mt-0">
                            Describe max. 3 custom topics you want to be moderated (for example: "Comments about our mobile app").
                          </div>
                          {customAiFilterInputs.map((input, index) => (
                            <div key={index} className="position-relative mb-2">
                              <input 
                                className="form-control inp-text inpt-text-aif"
                                type="text" 
                                value={input}
                                onChange={(e) => updateCustomAiFilterInput(index, e.target.value)}
                                placeholder="Comments about..."
                                maxLength={100}
                              />
                              <div className="d-flex justify-content-end mt-1">
                                <small className="aif-length">
                                  {input.length}/100
                                </small>
                              </div>
                              {customAiFilterInputs.length > 1 && index != 0 && (
                                <button
                                  type="button"
                                  className="btn btn-tr ai-var-btn"
                                  onClick={() => removeCustomAiFilterInput(index)}
                                >
                                  <FontAwesomeIcon icon={faXmark} />
                                </button>
                              )}
                            </div>
                          ))}

                          {customAiFilterInputs.length < 3 && (
                            <button type="button" className="btn btn-tr py-2 px-0 aif-more" onClick={addCustomAiFilterInput}>
                              <FontAwesomeIcon icon={faPlus} className="me-2" size="lg"/>
                              <span> Add more topics</span>
                            </button>
                          )}
                          
                          <div className="d-flex justify-content-between mt-1 mb-4">
                            <div className="sent-text-extra">
                            </div>
                            <div className='ml-2'>
                              <Button name="button" className="btn btn-primary save-blacklist" clickHandler={saveCustomAiFilter}>
                                Save
                                {loadingButtons.has('custom_ai_filter_desc') && <FontAwesomeIcon icon={faSpinnerThird} className="ms-2" spin={true}/>}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
            }
          </SwitchItem>

          <SwitchItem
              title="Hide impersonators"
              tooltip_place="bottom"
              value="true"
              checked={pageAttributes.hide_impersonators}
              name="page[hide_impersonators]"
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
              badge="AI"
          />

          {/* URL Settings */}
          <SwitchItem 
            title="Hide URL's"
            value="true"
            checked={pageAttributes.hide_urls}
            name="page[hide_urls]"
            disabled={pageAttributes.private_mode}
            changeHandler={handleChange}
          >
            {pageAttributes.hide_urls && (
              <AdvancedSettingsSection
                isAdvanced={pageAttributes.hide_urls_advanced}
                onEnableAdvanced={(e) => enableAdvancedOption(e, "hide_urls")}
                tags={pageAttributes.hide_urls_list}
                inputId="hide_urls_list"
                advDescId="URL's"
                placeholder="Type here (press enter after each url)"
                description="By default all URL's will be hidden, you can add domains or URL's you'd like to exclude from this filter."
                onSave={(e) => {
                  e.target.dataset.buttonId = 'hide_urls_list'
                  handleChange(e)
                }}
                showIcon={loadingButtons.has('hide_urls_list')}
              />
            )}
          </SwitchItem>

          {/* Email and Phone Settings */}
          {userAttributes.detect_email_phone && (
            <SwitchItem 
              title="Hide emails and phone numbers"
              value="true"
              checked={pageAttributes.detect_email_phone}
              name="page[detect_email_phone]"
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
            />
          )}

          {/* Mentions Settings */}
          <SwitchItem 
            title="Hide mentions"
            value="true"
            checked={pageAttributes.hide_mentions}
            name="page[hide_mentions]"
            disabled={pageAttributes.private_mode}
            changeHandler={handleChange}
          >
            {pageAttributes.hide_mentions && (
              <AdvancedSettingsSection
                isAdvanced={pageAttributes.hide_mentions_advanced}
                onEnableAdvanced={(e) => enableAdvancedOption(e, "hide_mentions")}
                tags={pageAttributes.hide_mentions_list}
                inputId="hide_mentions_list"
                advDescId="mentions"
                placeholder="Type here (press enter after each name of account handle)"
                description="Here you can add mentions you'd like to exclude from this filter."
                onSave={(e) => {
                  e.target.dataset.buttonId = 'hide_mentions_list'
                  handleChange(e)
                }}
                showIcon={loadingButtons.has('hide_mentions_list')}
              />
            )}
          </SwitchItem>

          {/* Hashtags Settings */}
          <SwitchItem 
            title="Hide hashtags"
            value="true"
            checked={pageAttributes.hide_hashtags}
            name="page[hide_hashtags]"
            disabled={pageAttributes.private_mode}
            changeHandler={handleChange}
          >
            {pageAttributes.hide_hashtags && (
              <AdvancedSettingsSection
                isAdvanced={pageAttributes.hide_hashtags_advanced}
                onEnableAdvanced={(e) => enableAdvancedOption(e, "hide_hashtags")}
                tags={pageAttributes.hide_hashtags_list}
                inputId="hide_hashtags_list"
                advDescId="hashtags"
                placeholder="Type here (press enter after each hashtag)"
                description="Here you can add hashtags you'd like to exclude from this filter."
                onSave={(e) => {
                  e.target.dataset.buttonId = 'hide_hashtags_list'
                  handleChange(e)
                }}
                showIcon={loadingButtons.has('hide_hashtags_list')}
              />
            )}
          </SwitchItem>

          {/* Images Settings */}
          {userAttributes.hide_images && (
            <SwitchItem 
              title="Hide images"
              value="true"
              checked={pageAttributes.hide_images}
              name="page[hide_images]"
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
            />
          )}

          {/* Emojis Settings */}
          {userAttributes.hide_emojis && (
            <SwitchItem 
              title="Hide emojis"
              value="true"
              checked={pageAttributes.hide_emojis}
              name="page[hide_emojis]"
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
            >
              {pageAttributes.hide_emojis && (
                <>
                  <div className="ml-4 mt-3 collapse show" />
                  <div className="row languages-select">
                    <div className="col-lg-6">
                      <select 
                        className="form-select" 
                        value={pageAttributes.emoji_option.toString()}
                        id="page[emoji_option]"
                        onChange={handleChange}
                      >
                        <option value="any">Hide comments containing any emoji</option>
                        <option value="specific">Hide comments containing specific emojis</option>
                        <option value="only_any">Hide emoji-only comments containing any emoji</option>
                        <option value="only_specific">Hide emoji-only comments containing specific emojis</option>

                      </select>
                    </div>
                  </div>
                  {pageAttributes.emoji_option.toString().includes("specific") && (
                    <div className="row languages-select mt-3">
                      <div className="col-12">
                        <div className="emoji-icon-wrap">
                          <FontAwesomeIcon 
                            icon={faFaceSmile}
                            className="mx-2 emoji-icon"
                            size="lg"
                            onClick={() => dispatch(setShowEmojiModal(true, "emoji_list"))}
                          />
                        </div>
                        <TagInput 
                          name="page[emoji_list]" 
                          tags={emojiList} 
                          inputId="emoji_list" 
                          placeholder="Enter your emojis here"
                        />
                        <div className="d-flex justify-content-end mt-1 mb-4">
                          <Button name="button" className="btn btn-primary save-blacklist" clickHandler={(e) => {
                            e.target.dataset.buttonId = 'emoji_list'
                            handleChange(e)
                          }}>
                            Save
                            {loadingButtons.has('emoji_list') && <FontAwesomeIcon icon={faSpinnerThird} className="ms-2" spin={true}/>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </SwitchItem>
          )}

          {/* Keywords Settings */}
          {userAttributes.blacklist && (
            <SwitchItem 
              title="Hide keywords"
              value="true"
              checked={pageAttributes.blacklist}
              name="page[blacklist]"
              outsideChild={true}
              disabled={pageAttributes.private_mode}
              changeHandler={handleChange}
            >
              {pageAttributes.blacklist && (
                <div className="form-check pl-2_5">
                  <div className="ml-4 mt-3 collapse show" />
                  <div className="row languages-select">
                    <div className="col-12">
                      <TagInput 
                        name="page[tags]" 
                        tags={pageAttributes.tags} 
                        copy={true}
                      />
                      <div className="d-flex justify-content-between mt-1 mb-4">
                        <div className="reg-check">
                          <input className="form-check-input ms-0"
                                 type="checkbox"
                                 defaultChecked={pageAttributes.single_keyword}
                                 value={pageAttributes.single_keyword}
                                 name="page[single_keyword]"
                                 onChange={handleChange}/>

                          <label className="form-check-label ms-2 ps-0">
                            Hide all single-word comments
                          </label>
                        </div>

                        <Button name="button" className="btn btn-primary save-blacklist" clickHandler={(e) => {
                          e.target.dataset.buttonId = 'tag-list'
                          handleChange(e)
                        }}>
                          Save
                          {loadingButtons.has('tag-list') && <FontAwesomeIcon icon={faSpinnerThird} className="ms-2" spin={true}/>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </SwitchItem>
          )}

          {/* Private Mode Settings */}
          {userAttributes.private_mode && (
            <>
              <div className="line mt-4" />
              <SwitchItem 
                title="Hide all comments"
                value="true"
                checked={pageAttributes.private_mode}
                name="page[private_mode]"
                changeHandler={handleChange}
              />
            </>
          )}

          <div className="line mt-4" />

          <SwitchItem
              title="Apply moderation to"
              desc="Choose to apply the moderation to posts only, ads only, or both."
              value="true"
              checked={pageAttributes.moderation_limit}
              name="page[moderation_limit]"
              changeHandler={handleChange}
          >
            {pageAttributes.moderation_limit && (
                <>
                  <div className="ml-4 mt-3 collapse show" />
                  <div className="row languages-select">
                    <div className="col-lg-3">
                      <select
                          className="form-select"
                          value={pageAttributes.moderation_limit_option.toString()}
                          id="page[moderation_limit_option]"
                          onChange={handleChange}
                      >
                        <option value="ads_and_posts">Both posts and ads</option>
                        <option value="ads_only">Ads only</option>
                        <option value="posts_only">Posts only</option>
                      </select>
                    </div>
                  </div>
                </>
            )}
          </SwitchItem>

          <div className="line mt-4" />

          {/* Delete Mode Settings */}
          <SwitchItem 
            title="Delete comments"
            desc="Automatically delete (instead of hide) comments that trigger your moderation settings."
            value="true"
            checked={pageAttributes.delete_mode}
            name="page[delete_mode]"
            wrapperClass="settings-item settings-item-alert"
            changeHandler={deleteModeHandler}
          >
            {pageAttributes.delete_mode && (
                <>
                <div className="ml-4 mt-3 collapse show" />
                <div className="row languages-select">
                  {pageAttributes.delete_mode_advanced ?
                      <div className="col-12">

                        <div className="form-group mb-3">
                          <Multiselect
                              customCloseIcon={<FontAwesomeIcon icon={faXmarkLarge} className="ml-2"/>}
                              placeholder={"Select filters"}
                              hidePlaceholder={deleteModeFilterIds.length > 0}
                              emptyRecordMsg="No other filters available..."
                              options={enabledFilterOptions}
                              selectedValues={enabledFilterOptions.filter(opt => deleteModeFilterIds.includes(opt.id))}
                              onSelect={addDeleteModeFilter}
                              onRemove={removeDeleteModeFilter}
                              displayValue="name"
                          />
                          {/* Hidden inputs for delete_mode_list */}
                          {deleteModeFilterIds.map((id) => (
                              <input
                                  key={id}
                                  type="hidden"
                                  name={`page[delete_mode_list][${id}]`}
                                  value={pageAttributes.enabled_filters[id]}
                              />
                          ))}

                          {/* When nothing selected, send empty hash + disable advanced */}
                          {deleteModeFilterIds.length === 0 && (
                              <>
                                <input
                                    type="hidden"
                                    name="page[delete_mode_list]"
                                    value=""
                                />
                                <input
                                    type="hidden"
                                    name="page[delete_mode_advanced]"
                                    value="false"
                                />
                              </>
                          )}

                          {/* When there *are* selections, ensure advanced stays enabled */}
                          {deleteModeFilterIds.length > 0 && (
                              <input
                                  type="hidden"
                                  name="page[delete_mode_advanced]"
                                  value="true"
                              />
                          )}

                          <div className="d-flex justify-content-between mt-1 mb-4">
                            <div className="sent-text-extra">
                              Here you can choose which moderation filters should delete comments instead of hiding it.
                            </div>
                            <div className='ml-2'>
                              <Button name="button" className="btn btn-primary save-blacklist" clickHandler={(e) => {
                                e.target.dataset.buttonId = 'delete_mode_list'
                                handleChange(e)
                              }}>
                                Save
                                {loadingButtons.has('delete_mode_list') && <FontAwesomeIcon icon={faSpinnerThird} className="ms-2" spin={true}/>}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div> :
                      <a href="#" className="fs-08" onClick={(e) => enableAdvancedOption(e, "delete_mode")}>
                        Advanced settings (by default delete mode applies to all moderation filters)
                      </a>
                  }
              </div>
              </>
            )}
          </SwitchItem>

          {/* Delete Mode Modal */}
          {showDeleteModeModal && (
            <DeleteModeModal
              onClose={closeDeleteModeModal}
              onSubmit={handleDeleteMode}
            />
          )}

          {/* Organic Moderation Settings */}
          {userAttributes.disable_organic && (
            <>
              <div className="line mt-4" />
              <SwitchItem 
                title="Disable organic moderation"
                desc="By default comments on both ads and organic content are moderated."
                value="true"
                checked={pageAttributes.disable_organic}
                name="page[disable_organic]"
                changeHandler={handleChange}
              />
            </>
          )}

          <div className="line mt-4" />

          <SwitchItem
              title="Whitelist"
              value="true"
              desc="Comments that contain keywords on your whitelist will not be hidden (or deleted)."
              checked={pageAttributes.whitelist_enable}
              name="page[whitelist_enable]"
              outsideChild={true}
              changeHandler={handleChange}
          >
            {pageAttributes.whitelist_enable && (
                <div className="form-check pl-2_5">
                  <div className="ml-4 mt-3 collapse show" />
                  <div className="row languages-select">
                    <div className="col-12">
                      <TagInput
                          name="page[whitelist]"
                          tags={pageAttributes.whitelist}
                          copy={true}
                      />
                      <div className="d-flex justify-content-between mt-1 mb-4">
                        <div className="sent-text-extra">
                        </div>
                        <Button name="button" className="btn btn-primary save-blacklist" clickHandler={(e) => {
                          e.target.dataset.buttonId = 'page[whitelist]'
                          handleChange(e)
                        }}>
                          Save
                          {loadingButtons.has('page[whitelist]') && <FontAwesomeIcon icon={faSpinnerThird} className="ms-2" spin={true}/>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
            )}
          </SwitchItem>

          <div className="line mt-4" />
        </form>

        {/* Test Tab */}
        {ability.can("test_comment","PagesController") && (
          <>
            <div className="fw-bold mt-4">Playground</div>
            <div className="mb-4">
              Enter a test comment to see if it would trigger your enabled moderation settings.
            </div>
            <TestTab admin={props.admin}/>
          </>
        )}

        {/* Import Section */}
        {props.admin && (hostname === "dev.commentguard.io" || hostname === "localhost") && (
          <ImportSection pageId={page.details.id} />
        )}

        <EmojiContainer 
          handleEmojiSelect={handleEmojiSelect} 
          clickOutside={clickOutsideEmoji}
        />
      </div>
      <ReactTooltip effect="solid" html={true} className="switch-tooltip"/>
    </>
  )
}

export default React.memo(Moderation)